import { Utils } from '../../../common/js/Utils';
import moment from 'moment';
import { Assumptions } from '../../../common/js/static/Assumptions';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { PolicyAmendmentProperty } from '../../../common/js/static/PolicyAmendmentProperty';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';
import { MMDirectApplication } from '../../classes/mmdirect_subclasses/MMDirectApplication';

export class CoverpathUtilsFrontEnd {
  static getUwAmendmentInfo(policy: CoverpathPolicy): string[] {
    const pendingUwAmendments = policy.getPendingUnderwriterAmendment();
    const productDescription = policy.isWholeLifePolicy()
      ? `Whole Life ${policy.termWithPendingAmendment()}`
      : `Convertible Term ${policy.termWithPendingAmendment()}`;
    const amendmentText = [];
    pendingUwAmendments.map((change) => {
      switch (change.property) {
        case PolicyAmendmentProperty.FACE_AMOUNT:
          amendmentText.push(
            `${productDescription} face amount changed from ${Utils.formatIntCurrency(
              change.previousValue
            )} to ${Utils.formatIntCurrency(change.newValue)}`
          );
          break;
        case PolicyAmendmentProperty.HAS_GIR_RIDER:
          amendmentText.push(`GIR (Guaranteed Insurability Rider) removed`);
          break;
        case PolicyAmendmentProperty.HAS_RTR_RIDER:
          amendmentText.push(`RTR (Renewable Term Rider) removed`);
          break;
        case PolicyAmendmentProperty.RTR_AMOUNT:
          if (change.getTypedNewValue() > 0) {
            amendmentText.push(
              `RTR (Renewable Term Rider) changed from ${Utils.formatIntCurrency(
                change.previousValue
              )} to ${Utils.formatIntCurrency(change.newValue)}`
            );
          }
          break;
        case PolicyAmendmentProperty.HAS_ALIR_RIDER:
          if (policy.hasAlirRider && policy.alirRider.lumpSumUses1035) {
            amendmentText.push(`1035 Exchange with ALIR removed`);
          } else {
            amendmentText.push(`Scheduled ALIR removed`);
          }
          break;
        case PolicyAmendmentProperty.ALIR_AMOUNT:
          amendmentText.push(
            `Scheduled ALIR amount changed from
              ${Utils.formatIntCurrency(change.previousValue)} to ${Utils.formatIntCurrency(change.newValue)}`
          );
          break;
        case PolicyAmendmentProperty.HAS_CIR_RIDER:
          amendmentText.push(`CIR (Chronic Illness Rider) removed`);
          break;
      }
    });

    const wasPrimaryWOPRemoved = !!pendingUwAmendments.find(
      (pch) => pch.property === PolicyAmendmentProperty.WAIVER && !pch.getTypedNewValue()
    );
    const wasPrimaryWOPAmountChanged = !!pendingUwAmendments.find(
      (pch) => pch.property === PolicyAmendmentProperty.WAIVER_AMOUNT && pch.getTypedNewValue() > 0
    );

    let wasSecondaryWOPRemoved = false;
    let wasSecondaryWOPAmountChanged = false;
    if (policy.application.isJointApp()) {
      const secondaryPolicy = policy.application.secondaryPolicy;
      wasSecondaryWOPRemoved = !!secondaryPolicy
        .getPendingUnderwriterAmendment()
        .find((pch) => pch.property === PolicyAmendmentProperty.WAIVER && !pch.getTypedNewValue());

      wasSecondaryWOPAmountChanged = !!secondaryPolicy
        .getPendingUnderwriterAmendment()
        .find((pch) => pch.property === PolicyAmendmentProperty.WAIVER_AMOUNT && pch.getTypedNewValue() > 0);
    }

    if (wasPrimaryWOPRemoved || wasSecondaryWOPRemoved) {
      amendmentText.push(`Waiver of Premium Rider removed`);
    }

    if (wasPrimaryWOPAmountChanged || wasSecondaryWOPAmountChanged) {
      const totalOldWaiver = policy.application.getTotalWaiverPremiumForRiders();
      const totalNewWaiver = policy.application.getTotalWaiverPremiumForRidersWithPendingAmendment();
      amendmentText.push(
        `Waiver of Premium Rider changed from ${Utils.formatCurrency(totalOldWaiver, 2)} to
          ${Utils.formatCurrency(totalNewWaiver, 2)}`
      );
    }

    return amendmentText;
  }

  static formatPrettyDateTime(rawDate: Date) {
    const formatter = new Intl.DateTimeFormat('en', {
      hour: 'numeric',
      minute: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return formatter.format(rawDate);
  }

  static getPendingDocusignCoin(
    application: CasApplication | MMDirectApplication,
    isDocuSignRedirectEnabled: boolean,
    inDocuSignStage: boolean,
    nonProdMode: boolean
  ): boolean {
    let isPlusLive = false;

    if (!application.primaryPolicy.isAnnuity()) {
      isPlusLive = /\+live-SendApplicationToDocuSign/.test(
        application.primaryPolicy.insured.person.residentialAddress.line1
      );
    }

    if (!nonProdMode) {
      return false;
    }

    if (!isPlusLive) {
      return false;
    }

    const metadata = application.primaryPolicy.getOwnerSigningPerson().esignMetadata;
    if (isDocuSignRedirectEnabled && metadata) {
      const submitted = !!metadata.docsGeneratedAt;
      const complete = metadata.ownerSignComplete;
      const retrieved = application.isDocuSignedFormRetrieved;
      return inDocuSignStage && submitted && complete && !retrieved;
    } else {
      return inDocuSignStage;
    }
  }

  static createBlobFromBase64(dataURI: string): Blob {
    const mime = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const binary = atob(dataURI.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
      array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: mime });
  }
}
